import { useEffect, useRef } from "react";
import { AUTOMATIC_SAVING } from "config/constants";
import { useMap, useMapEvents } from "react-leaflet";
import { useOptions } from "stores/optionsStore/OptionsContext";

import { useAuth } from "./useAuth";

export const useMapSettingsSaver = () => {
  const { update, user } = useAuth();
  const savingEnabled = useRef(false);
  const map = useMap();
  const {
    state: {
      mapColor,
      mapType: mapLayerID,
      mapOverlays: overlayType,
      language,
      units: unitType,
    },
  } = useOptions();
  const saveFnRef = useRef<() => void>();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!user) {
      savingEnabled.current = false;
      return undefined;
    }
    const timeout = setTimeout(() => {
      savingEnabled.current = true;
    }, AUTOMATIC_SAVING.allowSavingAfter);
    return () => clearTimeout(timeout);
  }, [user]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  saveFnRef.current = () => {
    const center = map.getCenter();
    const zoom = map.getZoom();
    void update({
      viewCenter: [center.lat, center.lng],
      mapColor,
      mapType: mapLayerID,
      mapOverlays: overlayType,
      language,
      units: unitType,
      viewZoom: zoom,
    });
  };

  useMapEvents({
    moveend: () => {
      if (!user) return;
      if (savingEnabled.current && saveFnRef.current) {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
          if (saveFnRef.current) {
            saveFnRef.current();
            saveTimeoutRef.current = undefined;
          }
        }, AUTOMATIC_SAVING.debounceLength);
      }
    },
  });
};
